import { useMemo, useCallback, useState } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import TopicContentViewerModal from './TopicContentViewerModal';
import type {
    TrainingPlan,
    TrainingSchedule,
    TrainingCompletion,
    TrainingEnrollment,
    PlanCategory,
    CategoryProgress,
} from '../../types/trainingTypes';
import { TOPIC_STYLE_COLORS, type TopicStyle } from '../../types/trainingTypes';
import {
    calculateDayNumberInCycle,
    getCompletionStatusBadge,
    parseLocalDateStr,
} from '../../utils/trainingUtils';
import '../../css/MyTrainingPage.css';

interface MyTrainingPageProps {
    onViewFullRecord?: () => void;
}

/**
 * Staff "My Training" view — shows today's topic, upcoming topics,
 * plan summary, progress by category, and recent activity.
 *
 * Validates: Requirements 15.1–15.7
 */
const MyTrainingPage = ({ onViewFullRecord }: MyTrainingPageProps) => {
    const {
        plans,
        topics,
        schedules,
        enrollments,
        completions,
        getCategoryProgress,
        recordCompletion,
    } = useSafetyTrainingContext();

    const { companyUser, companyOwner } = useUserContext();

    const currentUserId = companyUser?.id ?? companyOwner?.id ?? '';
    const companyId = (companyUser?.companyId as string) ?? companyOwner?.companyId ?? '';

    // -----------------------------------------------------------------------
    // Timezone-safe "today" (design decision #10)
    // -----------------------------------------------------------------------
    const todayLocal = useMemo(() => {
        const now = new Date();
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const d = String(now.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }, []);

    const todayDisplay = useMemo(
        () =>
            new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            }),
        [],
    );

    // -----------------------------------------------------------------------
    // User enrollment & active plan
    // -----------------------------------------------------------------------
    const userEnrollments = useMemo<TrainingEnrollment[]>(
        () => enrollments.filter(e => e.userId === currentUserId),
        [enrollments, currentUserId],
    );

    const activePlan = useMemo<TrainingPlan | null>(() => {
        const enrolledPlanIds = new Set(userEnrollments.map(e => e.planId));
        return plans.find(p => p.status === 'ACTIVE' && enrolledPlanIds.has(p.id)) ?? null;
    }, [plans, userEnrollments]);

    const activeEnrollment = useMemo<TrainingEnrollment | null>(() => {
        if (!activePlan) return null;
        return userEnrollments.find(e => e.planId === activePlan.id) ?? null;
    }, [activePlan, userEnrollments]);

    // Parse categories
    const categories = useMemo<PlanCategory[]>(() => {
        if (!activePlan) return [];
        try {
            return JSON.parse(activePlan.categories) as PlanCategory[];
        } catch {
            return [];
        }
    }, [activePlan]);

    // Day number in cycle
    const dayNumber = useMemo(() => {
        if (!activeEnrollment || !activePlan) return 0;
        const startDate = parseLocalDateStr(activeEnrollment.enrolledDate);
        return calculateDayNumberInCycle(startDate, new Date(), activePlan.cycleDuration);
    }, [activeEnrollment, activePlan]);

    // -----------------------------------------------------------------------
    // Schedules for this plan
    // -----------------------------------------------------------------------
    const planSchedules = useMemo<TrainingSchedule[]>(() => {
        if (!activePlan) return [];
        return schedules
            .filter(s => s.planId === activePlan.id)
            .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
    }, [schedules, activePlan]);

    // Today's schedule entries — support multiple topics per day
    // Only show entries assigned to everyone (null) or to this specific user
    const todaySchedules = useMemo<TrainingSchedule[]>(() => {
        return planSchedules
            .filter(s => {
                if (s.scheduledDate.split('T')[0] !== todayLocal) return false;
                return !s.assignedUserId || s.assignedUserId === currentUserId;
            })
            .sort((a, b) => {
                // Rescheduled items (locked=false) first, then by dayNumber
                const aRescheduled = a.locked === false ? 0 : 1;
                const bRescheduled = b.locked === false ? 0 : 1;
                if (aRescheduled !== bRescheduled) return aRescheduled - bRescheduled;
                return a.dayNumber - b.dayNumber;
            });
    }, [planSchedules, todayLocal]);

    // Primary today schedule (first uncompleted, or first overall)
    const todaySchedule = useMemo<TrainingSchedule | null>(() => {
        return todaySchedules[0] ?? null;
    }, [todaySchedules]);

    // Today's topic
    const todayTopic = useMemo(() => {
        if (!todaySchedule) return null;
        return topics.find(
            t => t.topicId === todaySchedule.topicId || t.id === todaySchedule.topicId,
        ) ?? null;
    }, [todaySchedule, topics]);

    // User completions for active plan
    const userCompletions = useMemo<TrainingCompletion[]>(() => {
        if (!activePlan) return [];
        return completions.filter(
            c => c.userId === currentUserId && c.planId === activePlan.id,
        );
    }, [completions, currentUserId, activePlan]);

    // Check if today's topic is already completed
    const todayCompletion = useMemo<TrainingCompletion | null>(() => {
        if (!todaySchedule) return null;
        return userCompletions.find(c => {
            const cDate = c.scheduleDate.split('T')[0];
            return cDate === todayLocal;
        }) ?? null;
    }, [userCompletions, todaySchedule, todayLocal]);

    // -----------------------------------------------------------------------
    // Up Next — upcoming topics after today
    // -----------------------------------------------------------------------
    const upNextItems = useMemo(() => {
        return planSchedules
            .filter(s => s.scheduledDate.split('T')[0] > todayLocal)
            .slice(0, 10)
            .map(s => {
                const topic = topics.find(
                    t => t.topicId === s.topicId || t.id === s.topicId,
                );
                const cat = categories.find(c => c.id === s.categoryId);
                return {
                    id: s.id,
                    date: s.scheduledDate.split('T')[0],
                    title: topic?.description ?? s.topicId,
                    style: (topic?.style ?? 'Other') as TopicStyle,
                    timeValue: topic?.timeValue ?? 0,
                    category: cat?.name ?? '—',
                };
            });
    }, [planSchedules, todayLocal, topics, categories]);

    // -----------------------------------------------------------------------
    // Plan summary stats
    // -----------------------------------------------------------------------
    const totalMinutesViewed = useMemo(
        () => userCompletions.reduce((sum, c) => sum + c.timeValueMinutes, 0),
        [userCompletions],
    );

    const percentViewed = useMemo(() => {
        if (!activePlan || activePlan.minimumTimeRequired === 0) return 0;
        return Math.min(100, Math.round((totalMinutesViewed / activePlan.minimumTimeRequired) * 100));
    }, [activePlan, totalMinutesViewed]);

    const daysRemaining = useMemo(() => {
        if (!activeEnrollment || !activePlan) return 0;
        const dueDate = activeEnrollment.nextDueDate
            ? parseLocalDateStr(activeEnrollment.nextDueDate)
            : (() => {
                const enrolled = parseLocalDateStr(activeEnrollment.enrolledDate);
                const due = new Date(enrolled);
                due.setDate(due.getDate() + activePlan.cycleDuration);
                return due;
            })();
        const now = new Date();
        return Math.max(0, Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    }, [activeEnrollment, activePlan]);

    const nextDueDateStr = useMemo(() => {
        if (!activeEnrollment || !activePlan) return '—';
        const dueDate = activeEnrollment.nextDueDate
            ? parseLocalDateStr(activeEnrollment.nextDueDate)
            : (() => {
                const enrolled = parseLocalDateStr(activeEnrollment.enrolledDate);
                const due = new Date(enrolled);
                due.setDate(due.getDate() + activePlan.cycleDuration);
                return due;
            })();
        return dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }, [activeEnrollment, activePlan]);

    const lastCompletionStr = useMemo(() => {
        if (activeEnrollment?.lastCompletionDate) {
            return new Date(activeEnrollment.lastCompletionDate).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', year: 'numeric',
            });
        }
        return '—';
    }, [activeEnrollment]);

    // -----------------------------------------------------------------------
    // Category progress
    // -----------------------------------------------------------------------
    const categoryProgress = useMemo<CategoryProgress[]>(() => {
        if (!activePlan) return [];
        return getCategoryProgress(currentUserId, activePlan.id);
    }, [activePlan, currentUserId, getCategoryProgress]);

    const overallPercentage = useMemo(() => {
        if (categoryProgress.length === 0) return 0;
        const totalTarget = categoryProgress.reduce((s, c) => s + c.targetMinutes, 0);
        const totalCompleted = categoryProgress.reduce((s, c) => s + c.completedMinutes, 0);
        return totalTarget > 0 ? Math.min(100, Math.round((totalCompleted / totalTarget) * 100)) : 0;
    }, [categoryProgress]);

    const totalRequired = useMemo(
        () => categoryProgress.reduce((s, c) => s + c.targetMinutes, 0),
        [categoryProgress],
    );

    const totalCompleted = useMemo(
        () => categoryProgress.reduce((s, c) => s + c.completedMinutes, 0),
        [categoryProgress],
    );

    // Pace projection
    const paceProjection = useMemo(() => {
        if (!activePlan || !activeEnrollment) return '';
        const cycleDays = activePlan.cycleDuration;
        const elapsed = cycleDays - daysRemaining;
        if (elapsed <= 0) return 'Just started';
        const rate = totalMinutesViewed / elapsed;
        const projected = rate * cycleDays;
        const projectedPct = activePlan.minimumTimeRequired > 0
            ? Math.round((projected / activePlan.minimumTimeRequired) * 100)
            : 0;
        const diff = projectedPct - 100;
        if (diff >= 0) return `On pace for: +${diff}% by due date`;
        return `On pace for: ${diff}% by due date`;
    }, [activePlan, activeEnrollment, daysRemaining, totalMinutesViewed]);

    // -----------------------------------------------------------------------
    // Recent Activity
    // -----------------------------------------------------------------------
    const recentActivity = useMemo(() => {
        return [...userCompletions]
            .sort((a, b) => b.completionDate.localeCompare(a.completionDate))
            .slice(0, 10)
            .map(c => {
                const topic = topics.find(
                    t => t.topicId === c.topicId || t.id === c.topicId,
                );
                const status = getCompletionStatusBadge(c.launched, c.markedAsViewed);
                return {
                    id: c.id,
                    date: new Date(c.completionDate).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric',
                    }),
                    name: topic?.description ?? c.topicId,
                    status,
                    minutes: c.timeValueMinutes,
                };
            });
    }, [userCompletions, topics]);

    // -----------------------------------------------------------------------
    // Actions — content must be viewed before marking as viewed
    // -----------------------------------------------------------------------
    const [showContentViewer, setShowContentViewer] = useState(false);

    const handleLaunchContent = useCallback(() => {
        setShowContentViewer(true);
    }, []);

    const handleMarkAsViewed = useCallback(async (signaturePath: string) => {
        if (!todaySchedule || !activePlan || todayCompletion) return;
        try {
            await recordCompletion({
                companyId,
                userId: currentUserId,
                topicId: todaySchedule.topicId,
                planId: activePlan.id,
                scheduleDate: todaySchedule.scheduledDate,
                completionDate: new Date().toISOString(),
                launched: true,
                markedAsViewed: true,
                timeValueMinutes: todayTopic?.timeValue ?? 0,
                categoryId: todaySchedule.categoryId ?? null,
                signaturePath,
            });
        } catch (err) {
            console.error('Failed to record completion:', err);
        }
    }, [
        todaySchedule, activePlan, todayCompletion, companyId,
        currentUserId, todayTopic, recordCompletion,
    ]);

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------
    const getStatusDotClass = (status: string): string => {
        if (status === 'Viewed') return 'viewed';
        if (status === 'Marked-Not Launched') return 'marked-not-launched';
        return 'not-viewed';
    };

    const formatUpNextDate = (dateStr: string): string => {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <div>
            {/* Page Header */}
            <div className="mtp-header">
                <div className="mtp-header-left">
                    <h1>My Training</h1>
                    <div className="mtp-header-meta">
                        <span>{todayDisplay}</span>
                        {activePlan && <span>{activePlan.name}</span>}
                        {dayNumber > 0 && activePlan && (
                            <span className="mtp-day-badge">
                                Day {dayNumber} of {activePlan.cycleDuration}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Today's Training Topics */}
            {activePlan ? (
                todaySchedules.length > 0 ? (
                    <div className="mtp-today-topics-container">
                        {todaySchedules.map((sched, idx) => {
                            const topic = topics.find(
                                t => t.topicId === sched.topicId || t.id === sched.topicId,
                            );
                            const cat = categories.find(c => c.id === sched.categoryId);
                            const completion = userCompletions.find(c =>
                                c.scheduleDate.split('T')[0] === todayLocal &&
                                c.topicId === sched.topicId
                            );
                            if (!topic) return null;
                            return (
                                <div key={sched.id} className={`mtp-today-card ${idx > 0 ? 'mtp-today-card-secondary' : ''}`}>
                                    <div className="mtp-today-card-header">
                                        <div>
                                            <div className="mtp-today-label">
                                                {todaySchedules.length > 1
                                                    ? `Today's Training — Topic ${idx + 1} of ${todaySchedules.length}`
                                                    : "Today's Training Topic"
                                                }
                                            </div>
                                            <h2 className="mtp-today-title">{topic.description}</h2>
                                            <div className="mtp-today-meta">
                                                <span className="mtp-meta-tag">
                                                    <span
                                                        className="mtp-style-dot"
                                                        style={{ background: TOPIC_STYLE_COLORS[topic.style] }}
                                                    />
                                                    {topic.style}
                                                </span>
                                                <span className="mtp-meta-tag">ID: {topic.topicId}</span>
                                                <span className="mtp-meta-tag">{topic.timeValue} min</span>
                                                {cat && (
                                                    <span className="mtp-meta-tag">{cat.name}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="mtp-today-actions">
                                            {completion ? (
                                                <button className="mtp-btn-mark completed">✓ Viewed &amp; Signed</button>
                                            ) : (
                                                <button className="mtp-btn-launch" onClick={handleLaunchContent}>
                                                    View Training
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="mtp-today-card">
                        <div className="mtp-no-topic">No topic scheduled for today</div>
                    </div>
                )
            ) : (
                <div className="mtp-today-card">
                    <div className="mtp-no-topic">
                        You are not enrolled in any active training plan
                    </div>
                </div>
            )}

            {/* Up Next */}
            {upNextItems.length > 0 && (
                <div className="mtp-section">
                    <div className="mtp-section-header">
                        <h3>Up Next</h3>
                    </div>
                    <div className="mtp-upnext-scroll">
                        {upNextItems.map(item => (
                            <div key={item.id} className="mtp-upnext-card">
                                <div className="mtp-upnext-date">{formatUpNextDate(item.date)}</div>
                                <div className="mtp-upnext-title">{item.title}</div>
                                <div className="mtp-upnext-tags">
                                    <span
                                        className="mtp-upnext-tag"
                                        style={{
                                            background: TOPIC_STYLE_COLORS[item.style] + '20',
                                            color: TOPIC_STYLE_COLORS[item.style],
                                        }}
                                    >
                                        {item.style}
                                    </span>
                                    <span className="mtp-upnext-tag">{item.timeValue} min</span>
                                    <span className="mtp-upnext-tag">{item.category}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Plan Summary */}
            {activePlan && (
                <div className="mtp-plan-summary">
                    <div className="mtp-plan-card">
                        <div className="mtp-plan-card-label">Plan</div>
                        <div className="mtp-plan-card-value" style={{ fontSize: '16px' }}>
                            {activePlan.name}
                        </div>
                        <div className="mtp-plan-card-detail">
                            {activePlan.governingStandard} · {activePlan.cycleDuration} days
                        </div>
                    </div>
                    <div className="mtp-plan-card">
                        <div className="mtp-plan-card-label">Next Due</div>
                        <div className="mtp-plan-card-value">{nextDueDateStr}</div>
                        <div className="mtp-plan-card-detail">{daysRemaining} days remaining</div>
                    </div>
                    <div className="mtp-plan-card">
                        <div className="mtp-plan-card-label">Last Completion</div>
                        <div className="mtp-plan-card-value">{lastCompletionStr}</div>
                        <div className="mtp-plan-card-detail">
                            {activeEnrollment?.lastCompletionDate ? 'Affidavit signed' : 'No completion yet'}
                        </div>
                    </div>
                    <div className="mtp-plan-card">
                        <div className="mtp-plan-card-label">Progress</div>
                        <div className="mtp-plan-card-value">{percentViewed}%</div>
                        <div className="mtp-plan-card-detail">
                            {totalMinutesViewed}/{activePlan.minimumTimeRequired} min
                        </div>
                    </div>
                </div>
            )}

            {/* Two-Column: Progress + Recent Activity */}
            <div className="mtp-content">
                {/* My Progress by Category */}
                <div className="mtp-card">
                    <div className="mtp-card-header">
                        <h3>My Progress by Category</h3>
                    </div>
                    <div className="mtp-card-body">
                        {categoryProgress.length > 0 ? (
                            <>
                                <div className="mtp-progress-overview">
                                    <div className="mtp-progress-stat">
                                        <span className="mtp-progress-stat-value">{overallPercentage}%</span>
                                        <span className="mtp-progress-stat-label">Overall</span>
                                    </div>
                                    <div className="mtp-progress-stat">
                                        <span className="mtp-progress-stat-value">{totalCompleted}</span>
                                        <span className="mtp-progress-stat-label">Viewed min</span>
                                    </div>
                                    <div className="mtp-progress-stat">
                                        <span className="mtp-progress-stat-value">{totalRequired}</span>
                                        <span className="mtp-progress-stat-label">Required min</span>
                                    </div>
                                    <div className="mtp-progress-stat">
                                        <span className="mtp-progress-stat-value">
                                            {Math.max(0, totalRequired - totalCompleted)}
                                        </span>
                                        <span className="mtp-progress-stat-label">Remaining min</span>
                                    </div>
                                </div>
                                {paceProjection && (
                                    <div className="mtp-pace-projection">{paceProjection}</div>
                                )}
                                {categoryProgress.map(cat => (
                                    <div key={cat.categoryId} className="mtp-category-row">
                                        <div className="mtp-category-row-header">
                                            <span className="mtp-category-name">{cat.categoryName}</span>
                                            <span className="mtp-category-stats">
                                                {Math.round(cat.percentage)}% · {cat.completedMinutes}/{cat.targetMinutes} min
                                            </span>
                                        </div>
                                        <div className="mtp-progress-bar-track">
                                            <div
                                                className={`mtp-progress-bar-fill ${cat.percentage >= 100 ? 'complete' : ''}`}
                                                style={{ width: `${Math.min(100, cat.percentage)}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </>
                        ) : (
                            <div className="mtp-empty-state">No categories defined</div>
                        )}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="mtp-card">
                    <div className="mtp-card-header">
                        <h3>Recent Activity</h3>
                    </div>
                    <div className="mtp-card-body">
                        {recentActivity.length > 0 ? (
                            <ul className="mtp-activity-list">
                                {recentActivity.map(item => (
                                    <li key={item.id} className="mtp-activity-item">
                                        <span className="mtp-activity-date">{item.date}</span>
                                        <span className={`mtp-activity-dot ${getStatusDotClass(item.status)}`} />
                                        <span className="mtp-activity-name">{item.name}</span>
                                        <span className="mtp-activity-minutes">{item.minutes} min</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="mtp-empty-state">No activity yet</div>
                        )}
                    </div>
                    {onViewFullRecord && (
                        <span className="mtp-view-record-link" onClick={onViewFullRecord}>
                            View full training record →
                        </span>
                    )}
                </div>
            </div>

            {/* Topic Content Viewer Modal */}
            <TopicContentViewerModal
                isOpen={showContentViewer}
                onClose={() => setShowContentViewer(false)}
                topic={todayTopic}
                onMarkAsViewed={todayCompletion ? undefined : handleMarkAsViewed}
                companyId={companyId}
            />
        </div>
    );
};

export default MyTrainingPage;
